<script>
import VimeoPlayerWithControls from './VimeoPlayerWithControls'
export default {
  name: 'AttachmentsView',
  components: { VimeoPlayerWithControls },
  props: {
    attachments: { type: [Object, Array], required: true },
    hideCaptions: Boolean,
    collapseCaption: Boolean,
    resizeBoxes: Boolean,
    maxH: null
  },

  data: () => ({
    loadedAttachments: {}
  }),

  computed: {
    singleVimeoVideo () {
      return this.attachmentsList.length === 1 && this.$refs['vimeo-player']
    },
    attachmentsList () {
      const attachments = this.attachments
      if (!attachments) return []
      const entries = Array.isArray(attachments)
        ? attachments.map((item, index) => [item.id || String(index), item])
        : Object.entries(attachments)
      if (!entries.length) return []
      return entries
        .sort((a, b) => (a[1].order || 0) - (b[1].order || 0))
        .reduce((res, [id, item]) => {
          if (item.srcSet) {
            const caption = this.hideCaptions ? '' : item.caption
            const { preview, full, original } = item.srcSet || {}
            let src
            if (item.type === 'embed/vimeo') {
              src = full
              res.push({
                id,
                type: item.type,
                vimeoId: item.name,
                url: src.url,
                dimensions: src.dimensions,
                caption: caption
              })
            } else if (item.type === 'embed/mixcloud') {
              src = full
              res.push({
                id,
                type: item.type,
                html: item.html,
                url: src.url,
                dimensions: src.dimensions,
                name: item.name,
                caption: caption
              })
            } else {
              src = full || (preview || {}).size > original.size ? preview : original
              res.push({
                id,
                type: item.type,
                url: src.url,
                dimensions: src.dimensions,
                caption: caption,
                name: item.name,
                original
              })
            }
            return res
          }
        }, [])
    }
  },

  methods: {
    setLoaded (id) {
      this.$set(this.loadedAttachments, id, true)
    },
    toggleVimeoPlay (value) {
      if (this.singleVimeoVideo) {
        this.$refs['vimeo-player'][0].toggleVimeoPlay(value)
      }
    },
    toggleVimeoSound (value) {
      if (this.singleVimeoVideo) {
        this.$refs['vimeo-player'][0].toggleVimeoSound(value)
      }
    },
    boxSizeStyle (item) {
      const { w, h } = item.dimensions || {}
      return { aspectRatio: `${w} / ${h}`, '--ar': w / h }
    }
  }
}
</script>

<template>
  <div class="attachments-view">
    <div v-for="item in attachmentsList" :key="item.id" class="attachment">
      <vimeo-player-with-controls
        ref="vimeo-player"
        v-if="item.type === 'embed/vimeo'"
        class="attachment-box"
        :style="boxSizeStyle(item)"
        :item="item"
        :hide-captions="hideCaptions"
        :collapse-caption="collapseCaption"
        @paused="$emit('paused', $event)"
        @muted="$emit('muted', $event)"
        @timeupdate="$emit('timeupdate', $event)"
        @progress="$emit('progress', $event)"
        @ready="setLoaded(item.id)" />
      <figure v-else class="attachment-box relative" :style="boxSizeStyle(item)">
        <div v-if="item.type === 'embed/mixcloud'" v-html="item.html" />
        <div v-else-if="item.type === 'embed/html'" class="embed-html-box">
          <div v-if="!loadedAttachments[item.id]" class="absolute flex inset-0 items-center justify-center">
            <span>Loading...</span>
          </div>
          <iframe :src="item.url" allowfullscreen :class="{ loaded: loadedAttachments[item.id] }"
            @load="setLoaded(item.id)" @error="setLoaded(item.id)" />
        </div>
        <img v-else :src="item.url" :alt="item.caption" :width="(item.dimensions || {}).w"
          :height="(item.dimensions || {}).h" :class="{
            outline: item.type === 'application/pdf',
            loaded: loadedAttachments[item.id],
            'bg-gray-400': !loadedAttachments[item.id]
          }" @load="setLoaded(item.id)" />
        <a v-if="item.type === 'application/pdf'"
          class="button compact absolute top-0 right-0 mt-sm mr-sm flex flex-col items-center justify-center hover:no-underline focus:no-underline bg-milk"
          :href="item.original.url" target="_blank" download>
          <i class="material-icons">get_app</i><span class="text-xs">PDF</span>
        </a>
        <figcaption v-if="item.caption && item.caption.trim()" class="caption text-xs">
          {{ item.caption.trim() }}
        </figcaption>
      </figure>
    </div>
  </div>
</template>

<style lang="scss">
.attachments-view {
  margin-bottom: var(--block-margin, var(--base-padding));

  .attachment:not(:last-child) {
    margin-bottom: var(--block-margin, var(--base-padding));
  }

  .attachment-box {
    width: min(100%, calc((var(--win-height, 100vh) - var(--base-size) - var(--footer-height)) * var(--ar)));
    width: min(100cqi, calc((100dvh - var(--base-size) - var(--footer-height)) * var(--ar)));

    &.vimeo-player-with-controls {
      width: min(100%, calc((var(--win-height, 100vh) - var(--base-size) * 2 - var(--footer-height)) * var(--ar)));
      width: min(100cqi, calc((100dvh - var(--base-size) * 2 - var(--footer-height)) * var(--ar)));
      height: auto;
    }

    img {
      object-fit: contain;
      width: 100%;
      height: 100%;

      &.loaded {
        background-color: transparent;
      }
    }
  }

  .embed-html-box {
    width: 100%;
    height: 100%;
    position: relative;
    background: #ccc;

    iframe {
      position: absolute;
      top: 0;
      left: 0;
      width: 100%;
      height: 100%;
      opacity: 0;
      transition: opacity 0.2s;

      &.loaded {
        opacity: 1;
      }
    }
  }

  .caption {
    line-height: 1.5em;
    padding-top: var(--small-padding);
    padding-bottom: var(--small-padding);
    padding-left: var(--caption-padding, 0);
    padding-right: var(--caption-padding, 0);
    // max-width: 85ch;
  }
}
</style>
